

import { Component, Vue } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

// @ts-ignore
import cloneDeep from "lodash/cloneDeep"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"
import FieldPermissions from "../Permissions/FieldPermissions.vue"
import RuleTrigger from "@/components/Config/Rules/RuleTrigger.vue"
import RuleActions from "@/components/Config/Rules/RuleActions.vue"

import { ActionSettingIds, NewValueSetting, Rule, RuleTemplate, DataType } from "@/types"
import User from "@/types/user"
import vSelect from "vue-select"

import api from "@/services/api"
import integrationApi from "@/services/api_Integrations"
import formatApi from "@/services/api/Formatting"

import { IntegrationOutOption } from "@/types/integration"

import StringValidation from "@/scripts/stringValidation"
import { Format } from "@/types/format"
import { TenantFeatures } from "@/types/tenantFeatures"

import { settingsValueToJsonString } from "@/utils/rulesUtils"
import { StoreModules } from "@/store/StoreModuleTypes"
import ConfigurationColumn from "@/types/configurationColumn"

const setup = namespace(StoreModules.Setup)

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"v-select": vSelect,
		"data-table": DataTable,
		"field-permissions": FieldPermissions,
		"skeleton": SkeletonLoader,
		"rule-trigger": RuleTrigger,
		"rule-action": RuleActions
	}
})
export default class RuleSetup extends Vue {
	$refs!: {
		dataTable: DataTable
	}

	@Getter("getMe")
	public me!: User;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getRecordOrCaseName")
	recordOrCaseName!: string;

	@setup.Getter("getFieldTypes")
	fieldTypes!: [];

	@setup.Getter("getDataSources")
	dataSources!: [];

	@setup.Getter("getDefinitions")
	definitions!: [];

	@setup.Getter("getFieldTypesById")
	fieldTypesById!: { [key: string]: any };

	@setup.Action loadRules!: () => Promise<any[]>;
	@setup.State rules!: Rule[];

	@setup.Mutation setRules!: (rules: Rule[]) => void;

	@setup.Action loadFieldsIfUnloaded!: () => Promise<void>

	public loading: boolean = true;
	private loadingRules: boolean = false;

	TenantFeatures = TenantFeatures

	private triggersValid: boolean = true;
	private actionsValid: boolean = true;
	private isDuplicationMode: boolean = false

	private integrationOuts: IntegrationOutOption[] = []
	private formats: Format[] | null = null

	private appliedTemplates: RuleTemplate [] = []

	private templates: RuleTemplate [] = []

	public get columns (): ConfigurationColumn[] {
		const columns: ConfigurationColumn[] = [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title)
			},
			{
				key: "triggers",
				label: "Triggers",
				dataType: "list",
				sortable: true,
				hasTemplate: true,
				inModal: (item: any) => false
			},
			{
				key: "actions",
				label: "Actions",
				dataType: "list",
				sortable: true,
				hasTemplate: true,
				inModal: (item: any) => false
			},
			{
				key: "applyToTemplates",
				label: "Templates",
				dataType: "list",
				sortable: true,
				hasTemplate: true,
				inModal: (item: any) => false,
				inTable: this.getIsFeatureEnabled(TenantFeatures.RuleTemplateEditing)
			}
		]

		if (this.rulesDuplicationEnabled) {
			columns.push({
				key: "duplicate",
				label: "Duplicate",
				sortable: false,
				hasTemplate: true,
				inModal: (item: any) => false // hide the edit for this column in the modal.
			} as ConfigurationColumn)
		}

		return columns
	}

	private get templateCloseOnSelect (): boolean {
		return !this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen)
	}

	private get rulesDuplicationEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.RulesEnableDuplication)
	}

	public async mounted (): Promise<void> {
		this.loadFieldsIfUnloaded()
		this.loadingRules = true
		await this.loadRules()
		if (this.getIsFeatureEnabled(TenantFeatures.RuleTemplateEditing)) {
			this.templates = (await api.categories()).map(t => { return { id: t.id, title: t.title, enabledOnCase: false, enabledOnRecord: false } })
		}

		integrationApi.enabledOutputs().then(outputs => {
			this.integrationOuts = outputs
		})

		this.loadingRules = false
		this.loading = false
	}

	private async openDuplicationMode (rule: Rule): Promise<void> {
		const duplicatedRule: Rule = await this.loadRule(rule)
		// update the title and remove the id
		duplicatedRule.title = `${rule.title} [Duplicate]`
		delete (duplicatedRule).id

		duplicatedRule.applyToTemplates.forEach((ruleTemplate: RuleTemplate) => {
			ruleTemplate.enabledOnCase = ruleTemplate.enabledOnRecord = false
		})

		this.isDuplicationMode = true
		this.$refs.dataTable.$refs.dataModal.open(duplicatedRule)
	}

	private async saveRule (rule: Rule): Promise<void> {
		// Clone the original so that changes don't affect data bindings.
		const saveRule = cloneDeep(rule)
		saveRule.applyToTemplates = this.appliedTemplates
		// Ensure that all values are strings.
		saveRule.triggers.forEach(t => {
			t.value = t.value.toString()
		})

		saveRule.actions.forEach(a => {
			settingsValueToJsonString(a, ActionSettingIds.RecipientList)
			settingsValueToJsonString(a, ActionSettingIds.CCList)
			settingsValueToJsonString(a, ActionSettingIds.ExcludeRecipientList)

			switch (a.settings[ActionSettingIds.NewValueSetting]) {
				case NewValueSetting.Manual.toString():
					if (a.settings[ActionSettingIds.FieldDataType] === DataType.userRole.toString()) {
						settingsValueToJsonString(a, ActionSettingIds.NewValue)
					}

					break
				case NewValueSetting.Expression.toString():
					settingsValueToJsonString(a, ActionSettingIds.NewValue)
					break
				default:
					break
			}
		})

		await api.ruleSave(saveRule)
		await this.loadRules()

		this.isDuplicationMode = false
	}

	private async deleteRule (rule: Rule): Promise<void> {
		await api.ruleDelete(rule)
		await this.loadRules()
	}

	private createNewItem (): Rule {
		this.templates.forEach(template => {
			template.enabledOnRecord = template.enabledOnCase = true
		})

		this.appliedTemplates = []

		return {
			title: "",
			triggers: [],
			actions: [],
			applyToTemplates: [],
			runOnce: false,
			tenantId: "" // populated by the DS for a new rule
		}
	}

	private loadRule (rule: Rule) : Rule {
		const clone = cloneDeep(rule)

		if (clone.actions) {
			clone.actions.forEach(a => {
				if (a.settings["recipientList"]) {
					a.settings["recipientList"] = JSON.parse(a.settings["recipientList"] as string)
				}
				if (a.settings["ccList"]) {
					a.settings["ccList"] = JSON.parse(a.settings["ccList"] as string)
				}
				if (a.settings["excludeRecipientList"]) {
					a.settings["excludeRecipientList"] = JSON.parse(a.settings["excludeRecipientList"] as string)
				}
			})
		}

		this.setAppliedTemplates(clone)

		return clone
	}

	private cancelEdit () : void {
		// reset duplication mode
		this.isDuplicationMode = false
	}

	private setTriggersValid (valid: boolean): void {
		this.triggersValid = valid
		this.TriggersAndActionsDataValid()
	}

	private setActionsValid (valid: boolean): void {
		this.actionsValid = valid
		this.TriggersAndActionsDataValid()
	}

	private TriggersAndActionsDataValid (): void {
		if (this.triggersValid && this.actionsValid) {
			this.$refs.dataTable.$refs.dataModal.saveButtonDisabled = false
		} else {
			this.$refs.dataTable.$refs.dataModal.saveButtonDisabled = true
		}
	}

	private async formatsLoad (): Promise<Format[]> {
		if (this.formats) return this.formats!

		const formats = await formatApi.formatGet()
		this.formats = formats
		return formats
	}

	private async setAppliedTemplates (rule: Rule) {
		this.appliedTemplates = []
		this.appliedTemplates = rule.applyToTemplates

		const added = this.appliedTemplates.reduce((addedTemplates, current) => {
			addedTemplates[current.id!] = current.enabledOnCase || false
			return addedTemplates
		}, {} as { [key: string]: boolean })

		this.templates.forEach(template => {
			template.enabledOnCase = added[template.id!] !== undefined ? added[template.id!] : true
			template.enabledOnRecord = template.enabledOnCase
		})
	}

	private setEnabledCase (id: string, value: boolean) {
		this.appliedTemplates.filter(t => t.id === id).forEach(t => {
			t.enabledOnCase = !t.enabledOnCase
			t.enabledOnRecord = (t.enabledOnRecord !== null) ? t.enabledOnCase : null
		})
	}

	private removeTemplate (id: string) {
		this.appliedTemplates.removeWhere(t => t.id === id)
	}
}
